import React, { useState, useRef, useEffect } from 'react';
import { X, Eye, Shield, LogOut, UserPlus, UserCircle } from 'lucide-react';
import { useUser } from '../../../contexts/UserContext';
import InviteViewerModal from '../../auth/InviteViewerModal';

import { logger } from '@/utils/logger';

interface FloatingSessionControlProps {
  user: any;
  isAdmin: boolean;
  viewerMode: boolean;
  logout: () => Promise<void>;
}

const FloatingSessionControl: React.FC<FloatingSessionControlProps> = ({
  user,
  isAdmin,
  viewerMode,
  logout
}) => {
  const { viewAsViewer, backToAdmin } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Close expanded menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  /**
   * Toggle viewer mode for admins
   * Allows admins to view the app as viewers would see it
   */
  const handleViewerModeToggle = () => {
    if (viewerMode) {
      backToAdmin();
    } else {
      viewAsViewer();
    }
    setIsExpanded(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  // Always show FloatingSessionControl for authenticated users (including viewers)
  // The user object should always be set if authenticated, but we double-check here
  if (!user) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50" ref={containerRef}>
      <button
        onClick={toggleExpanded}
        className={`w-12 h-12 theme-surface theme-text border theme-border rounded-full shadow-elev1 hover:shadow-elev2 hover:border-[color-mix(in_srgb,var(--accent-primary)_28%,var(--border)_72%)] transition-all duration-200 flex items-center justify-center ${
          isExpanded ? 'scale-110' : 'scale-100'
        }`}
        title="Session options"
        aria-label="Session options"
      >
        {isExpanded ? <X size={20} /> : <UserCircle size={22} />}
      </button>

      {isExpanded && (
        <div className="absolute bottom-16 right-0 theme-surface rounded-lg shadow-xl border theme-border p-4 min-w-[200px]">
          {isAdmin && (
            <>
              <button
                onClick={handleViewerModeToggle}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:theme-muted rounded-md transition-colors mb-2"
              >
                {viewerMode ? (
                  <>
                    <Shield size={16} className="text-blue-600" />
                    <span className="text-blue-600 font-medium">Back to Admin</span>
                  </>
                ) : (
                  <>
                    <Eye size={16} className="theme-text-muted" />
                    <span className="theme-text-muted">Viewer Mode</span>
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setIsInviteModalOpen(true);
                  setIsExpanded(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:theme-muted rounded-md transition-colors mb-2"
              >
                <UserPlus size={16} className="theme-text" />
                <span className="theme-text">Invite Viewer</span>
              </button>
            </>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:theme-muted rounded-md transition-colors text-red-600"
          >
            <LogOut size={16} />
            <span>Log Out</span>
          </button>
        </div>
      )}

      <InviteViewerModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />
    </div>
  );
};

export default FloatingSessionControl;
